import React from "react";
import { Question, Answer } from "../types";
import "./QuestionsSummary.scss";

interface QuestionsSummaryProps {
  questions: Question[];
  answers: Answer[];
  onContinue: () => void;
  onEditQuestion?: (questionId: number) => void;
  translate: (key: string) => string;
  isLoading?: boolean;
  locale?: string;
}

// Campos que contienen fechas y deben formatearse
const DATE_FIELDS = ["effectdate", "birthdate", "date", "startdate", "enddate"];

// Campos que contienen valores monetarios
const CURRENCY_FIELDS = ["capital", "amount", "price", "income", "salary"];

const QuestionsSummary: React.FC<QuestionsSummaryProps> = ({
  questions,
  answers,
  onContinue,
  onEditQuestion,
  translate,
  isLoading = false,
  locale = "es-ES",
}) => {
  /**
   * Formatea una fecha en formato legible
   */
  const formatDate = (value: string): string => {
    try {
      // Intentar parsear diferentes formatos de fecha
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        // Si no es una fecha válida, intentar formato DD/MM/YYYY o YYYY-MM-DD
        const parts = value.split(/[-/]/);
        if (parts.length === 3) {
          const [first, second, third] = parts;
          // Si el primer elemento tiene 4 dígitos, es YYYY-MM-DD
          if (first.length === 4) {
            return new Date(`${first}-${second}-${third}`).toLocaleDateString(locale, {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
          }
          // Si no, asumimos DD/MM/YYYY
          return new Date(`${third}-${second}-${first}`).toLocaleDateString(locale, {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        }
        return value;
      }
      return date.toLocaleDateString(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return value;
    }
  };

  /**
   * Formatea un valor monetario
   */
  const formatCurrency = (value: string): string => {
    try {
      const numValue = parseFloat(value.replace(/[^\d.-]/g, ""));
      if (isNaN(numValue)) return value;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: locale.startsWith("en") ? "GBP" : "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numValue);
    } catch {
      return value;
    }
  };

  /**
   * Formatea valores booleanos
   */
  const formatBoolean = (value: string): string => {
    const positiveValues = ["true", "yes", "si", "sí", "1", "oui"];
    const isPositive = positiveValues.includes(value.toLowerCase());
    return isPositive
      ? translate("questions.summary.yes") || "Sí"
      : translate("questions.summary.no") || "No";
  };

  /**
   * Obtiene el label formateado de una respuesta según el tipo de pregunta
   */
  const getAnswerLabel = (question: Question, answerValue: string): string => {
    // Si el valor es "none", mostrar la traducción de "Ninguna de las anteriores"
    if (answerValue === "none") {
      return translate("productQuestions.label.noNone") || "Ninguna de las anteriores";
    }

    // Si tiene opciones, buscar el label correspondiente
    if (question.options && question.options.length > 0) {
      const option = question.options.find((opt) => opt.value === answerValue);
      if (option?.label) return option.label;
    }

    const fieldName = question.name.toLowerCase();

    // Formatear fechas
    if (
      question.type === "calendar_extended" ||
      DATE_FIELDS.some((df) => fieldName.includes(df))
    ) {
      return formatDate(answerValue);
    }

    // Formatear valores booleanos
    if (question.type === "boolean_question") {
      return formatBoolean(answerValue);
    }

    // Formatear valores monetarios
    if (CURRENCY_FIELDS.some((cf) => fieldName.includes(cf))) {
      return formatCurrency(answerValue);
    }

    return answerValue;
  };

  /**
   * Obtiene la respuesta correspondiente a una pregunta
   */
  const getAnswerForQuestion = (questionId: number): Answer | undefined => {
    return answers.find((a) => a.question_id === questionId);
  };

  /**
   * Detecta si un valor es booleano (string o boolean real)
   */
  const isBooleanValue = (value: unknown): boolean => {
    if (typeof value === "boolean") return true;
    if (typeof value === "string") {
      const booleanValues = ["true", "false", "yes", "no", "si", "sí", "oui", "non"];
      return booleanValues.includes(value.toLowerCase());
    }
    return false;
  };

  /**
   * Formatea el label: si es array, une los elementos con ", "
   */
  const formatLabel = (label: string | string[]): string => {
    if (Array.isArray(label)) {
      return label.join(", ");
    }
    return label;
  };

  /**
   * Obtiene el valor a mostrar: label si existe, sino value con formateo
   */
  const getDisplayValue = (question: Question, answer: Answer): string => {
    // Si tiene label y no es un valor booleano, usarlo directamente
    if (answer.label && !isBooleanValue(answer.label)) {
      return formatLabel(answer.label);
    }

    // Si no tiene value, retornar vacío
    if (answer.value === null || answer.value === undefined) {
      return "";
    }

    // Si el value es un valor booleano (boolean real o string), formatearlo
    if (isBooleanValue(answer.value)) {
      const stringValue = String(answer.value);
      return formatBoolean(stringValue);
    }

    // Aplicar formateo según el tipo de pregunta
    return getAnswerLabel(question, answer.value);
  };

  const answeredQuestions = questions.filter((question) => {
    const answer = getAnswerForQuestion(question.id);
    return answer && (answer.label !== null || answer.value !== null);
  });

  return (
    <div className="questions-summary">
      <div className="questions-summary__header">
        <h2 className="questions-summary__title">
          {translate("questions.summary.title") || "Resumen de tus respuestas"}
        </h2>
        <p className="questions-summary__subtitle">
          {translate("questions.summary.subtitle") ||
            "Revisa tus respuestas antes de continuar"}
        </p>
      </div>

      <div className="questions-summary__list">
        {answeredQuestions.map((question) => {
          const answer = getAnswerForQuestion(question.id)!;
          const displayValue = getDisplayValue(question, answer);

          return (
            <div key={question.id} className="questions-summary__item">
              <div className="questions-summary__item-content">
                <p className="questions-summary__question">{question.content}</p>
                <p className="questions-summary__answer">{displayValue}</p>
              </div>
              {onEditQuestion && (
                <button
                  className="questions-summary__edit-btn"
                  onClick={() => onEditQuestion(question.id)}
                  type="button"
                >
                  {translate("questions.summary.edit") || "Editar"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="questions-summary__actions">
        <button
          className="questions-summary__continue-btn"
          onClick={onContinue}
          disabled={isLoading}
          type="button"
        >
          {isLoading
            ? translate("questions.summary.loading") || "Cargando..."
            : translate("questions.summary.continue") || "Continuar"}
        </button>
      </div>
    </div>
  );
};

export default QuestionsSummary;
